import {
  Box,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from 'react-admin';
import { EmptyContent } from 'src/components/empty-content';
import AddressField from '../../components/AddressField';
import XCurrencyField from '../../components/XCurrencyField';
import {
  DexPositionRow,
  HyperliquidPosition,
  SelectedWallet,
  fetchHyperliquidWalletPositions,
  toNumber,
} from '../../services/hyperliquidUserService';

const getLeverageLabel = (leverage?: HyperliquidPosition['leverage']) => {
  if (!leverage?.value) return '-';
  return leverage.type ? `${leverage.value}x (${leverage.type})` : `${leverage.value}x`;
};

const getSide = (size?: string) => {
  const value = toNumber(size);
  if (value > 0) return { label: 'Long', color: 'success' as const };
  if (value < 0) return { label: 'Short', color: 'error' as const };
  return { label: 'Flat', color: 'default' as const };
};

const DexPositions = ({ selectedWallet }: { selectedWallet: SelectedWallet[] }) => {
  const t = useTranslate();
  const [positions, setPositions] = useState<DexPositionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const walletAddresses = useMemo(
    () => Array.from(new Set((selectedWallet ?? []).map((wallet) => wallet.walletAddress).filter(Boolean))),
    [selectedWallet]
  );

  useEffect(() => {
    let active = true;

    if (walletAddresses.length === 0) {
      setPositions([]);
      setHasError(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    setHasError(false);

    Promise.allSettled(walletAddresses.map((walletAddress) => fetchHyperliquidWalletPositions(walletAddress)))
      .then((results) => {
        if (!active) return;

        const nextPositions = results.flatMap((result) =>
          result.status === 'fulfilled' ? result.value : []
        );

        const failedCount = results.filter((result) => result.status === 'rejected').length;

        setPositions(nextPositions);
        setHasError(failedCount > 0 && nextPositions.length === 0);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [walletAddresses]);

  if (loading) {
    return <Skeleton height={320} width="100%" />;
  }

  if (hasError) {
    return (
      <EmptyContent
        title={t('common.noData', { _: 'No data' })}
        description={t('module.userInfo.dexPositions.fetchFailed', {
          _: 'Failed to load positions from Hyperliquid',
        })}
      />
    );
  }

  if (walletAddresses.length === 0 || positions.length === 0) {
    return (
      <EmptyContent
        title={t('common.noData', { _: 'No data' })}
        description={t('module.userInfo.dexPositions.empty', {
          _: 'No open positions for the selected DEX wallets',
        })}
      />
    );
  }

  return (
    <TableContainer>
      <Table
        sx={{
          '& .MuiTableCell-root': {
            whiteSpace: 'nowrap',
          },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('module.userInfo.wallet.address')}</TableCell>
            <TableCell>{t('module.userInfo.coin')}</TableCell>
            <TableCell>{t('module.userInfo.side', { _: 'Side' })}</TableCell>
            <TableCell>{t('module.userInfo.size')}</TableCell>
            <TableCell>{t('module.userInfo.dexPositions.entryPrice', { _: 'Entry Price' })}</TableCell>
            <TableCell>{t('module.userInfo.dexPositions.positionValue', { _: 'Position Value' })}</TableCell>
            <TableCell>{t('module.userInfo.dexPositions.unrealizedPnl', { _: 'Unrealized PnL' })}</TableCell>
            <TableCell>{t('module.userInfo.dexPositions.liquidationPrice', { _: 'Liquidation Price' })}</TableCell>
            <TableCell>{t('module.userInfo.dexPositions.leverage', { _: 'Leverage' })}</TableCell>
            <TableCell>{t('module.userInfo.dexPositions.roe', { _: 'ROE' })}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map((position) => {
            const side = getSide(position.szi);

            return (
              <TableRow key={position.id} hover>
                <TableCell>
                  <AddressField address={position.walletAddress} sx={{ width: '180px' }} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {position.coin || '-'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={side.label} color={side.color} size="small" />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={Math.abs(toNumber(position.szi))}
                    currency={{ symbol: `(${position.coin ?? ''})`, position: 'after', space: true }}
                  />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(position.entryPx)}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(position.positionValue)}
                    currency={{ symbol: '$', position: 'before' }}
                  />
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(position.unrealizedPnl)}
                    currency={{ symbol: '$', position: 'before' }}
                    colorize
                  />
                </TableCell>
                <TableCell>
                  {position.liquidationPx ? (
                    <XCurrencyField
                      value={toNumber(position.liquidationPx)}
                      currency={{ symbol: '$', position: 'before' }}
                    />
                  ) : (
                    <Box sx={{ px: 1 }}>
                      <Typography variant="body2">-</Typography>
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{getLeverageLabel(position.leverage)}</Typography>
                </TableCell>
                <TableCell>
                  <XCurrencyField
                    value={toNumber(position.returnOnEquity)}
                    currency={{ symbol: '%', position: 'after' }}
                    colorize
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DexPositions;
